import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {CheckCircle2, Sparkles, GraduationCap, Award, Heart, ArrowRight} from 'lucide-react'
import SobreMonica from '../components/SobreMonica'

// origemFixa define a origem da inscrição desta landing:
// - 'Capacitacao (negocio)' (padrão, rota /capacitacao)
// - 'Capacitacao Equipe' (rota /capacitacao-equipe)
function Capacitacao({ origemFixa = 'Capacitacao (negocio)' }: { origemFixa?: string }) {
  const [indicadoPor, setIndicadoPor] = useState<string>('')

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const indicado = params.get('indicado_por')
    if (indicado && indicado.trim()) {
      const valor = indicado.trim()
      setIndicadoPor(valor)
      try {
        sessionStorage.setItem('capacitacao_indicado_por', valor)
      } catch {
        // ignore storage errors
      }
    }
    // Persistir a origem para o fluxo de seleção
    try {
      sessionStorage.setItem('capacitacao_origem', origemFixa)
    } catch {
      // ignore storage errors
    }
  }, [origemFixa])

  const linkSelecao = (() => {
    const sp = new URLSearchParams()
    if (indicadoPor) sp.set('indicado_por', indicadoPor)
    sp.set('origem', origemFixa)
    return `/selecao?${sp.toString()}`
  })()

  return (
    <div className="min-h-screen bg-[#F8F4EF]">
      {/* Header */}
      <header className="bg-white border-b border-[#EDE0D4] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="font-cormorant text-lg sm:text-xl font-semibold text-[#2D6A4F] truncate">
            Diagnóstico de Saúde e Emoções
          </div>
          <nav className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Link
              to="/admin"
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-[#2D6A4F] hover:bg-[#D8F3DC] rounded-full transition-colors"
            >
              Entrar
            </Link>
            <Link
              to="/assinar"
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium bg-[#2D6A4F] text-white rounded-full hover:bg-[#1f4f3a] transition-colors"
            >
              Já sou consultora
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 sm:px-6 pt-12 sm:pt-20 pb-12 sm:pb-16">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#D8F3DC] text-[#2D6A4F] text-xs sm:text-sm font-medium mb-6">
            Seleção aberta · Vagas limitadas
          </span>
          <h1 className="font-cormorant text-3xl sm:text-5xl md:text-6xl font-semibold text-[#2C2C2A] leading-[1.15] mb-6">
            Você quer trabalhar com bem-estar, saúde física e emocional e ter uma renda crescente e escalável?
          </h1>
          <p className="text-base sm:text-lg text-[#5F5E5A] leading-relaxed max-w-2xl mx-auto mb-8">
            Inscreva-se na seleção e tenha acesso gratuito ao curso introdutório de óleos essenciais com certificado.
          </p>
          <Link
            to={linkSelecao}
            className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white text-base sm:text-lg font-medium px-8 py-4 rounded-full hover:bg-[#1f4f3a] transition-all shadow-lg hover:shadow-xl"
          >
            Quero me inscrever na seleção
            <ArrowRight size={20} />
          </Link>
          {indicadoPor && (
            <p className="mt-4 text-sm text-[#5F5E5A]">
              Indicada por <strong className="text-[#2D6A4F]">{indicadoPor}</strong>
            </p>
          )}
        </div>
      </section>

      {/* O que você vai receber */}
      <section className="px-4 sm:px-6 py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <Sparkles className="mx-auto text-[#52B788] mb-3" size={32} />
            <h2 className="font-cormorant text-2xl sm:text-4xl font-semibold text-[#2C2C2A] mb-3">
              O que você vai receber
            </h2>
            <p className="text-[#5F5E5A] max-w-2xl mx-auto">
              Um caminho completo para você se conectar com os óleos essenciais e construir uma nova renda.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {[
              'Seleção personalizada de perfil',
              'Acesso ao curso Uma Gota de Equilíbrio com certificado',
              '7 aulas introdutórias sobre óleos essenciais',
              'Certificado de Aromaterapia Introdutória',
              'Suporte exclusivo',
            ].map((item) => (
              <div
                key={item}
                className="flex items-start gap-3 bg-[#F8F4EF] rounded-2xl p-5 border border-[#EDE0D4]"
              >
                <CheckCircle2 className="text-[#52B788] shrink-0 mt-0.5" size={22} />
                <span className="text-[#2C2C2A] text-sm sm:text-base leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Para quem é */}
      <section className="px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <Heart className="mx-auto text-[#B08968] mb-3" size={32} />
            <h2 className="font-cormorant text-2xl sm:text-4xl font-semibold text-[#2C2C2A] mb-3">
              Para quem é
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            {[
              'Pessoas que nunca usaram óleos essenciais',
              'Pessoas que já usam mas não são cadastradas na dōTERRA',
              'Pessoas que querem uma renda extra na área de saúde e bem-estar',
              'Pessoas abertas a aprender uma nova forma de se conectar com as pessoas',
            ].map((item) => (
              <div
                key={item}
                className="bg-white rounded-2xl p-5 sm:p-6 border border-[#EDE0D4] flex items-start gap-3 shadow-sm"
              >
                <ArrowRight className="text-[#2D6A4F] shrink-0 mt-1" size={20} />
                <p className="text-[#2C2C2A] text-sm sm:text-base leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Como funciona */}
      <section className="px-4 sm:px-6 py-12 sm:py-16 bg-[#2D6A4F] text-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <GraduationCap className="mx-auto text-[#D8F3DC] mb-3" size={32} />
            <h2 className="font-cormorant text-2xl sm:text-4xl font-semibold mb-3">
              Como funciona
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              Um processo simples para te guiar do zero até receber seu certificado.
            </p>
          </div>
          <ol className="space-y-3 sm:space-y-4">
            {[
              'Preencha o formulário de seleção',
              'Assista às 7 aulas do curso',
              'Responda o quiz de cada aula',
              'Receba seu certificado',
            ].map((item, i) => (
              <li
                key={item}
                className="flex items-start gap-4 bg-white/10 backdrop-blur rounded-2xl p-4 sm:p-5 border border-white/20"
              >
                <span className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white text-[#2D6A4F] font-cormorant font-semibold text-lg flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="text-sm sm:text-base leading-relaxed pt-1.5">{item}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Sobre a criadora */}
      <SobreMonica variante="capacitacao" />

      {/* CTA Final */}
      <section className="px-4 sm:px-6 py-14 sm:py-20">
        <div className="max-w-3xl mx-auto text-center bg-white rounded-3xl p-8 sm:p-12 border border-[#EDE0D4] shadow-sm">
          <Award className="mx-auto text-[#B08968] mb-4" size={36} />
          <h2 className="font-cormorant text-2xl sm:text-4xl font-semibold text-[#2C2C2A] mb-4">
            Pronto(a) para começar essa nova jornada?
          </h2>
          <p className="text-[#5F5E5A] mb-8 max-w-xl mx-auto leading-relaxed">
            Inscreva-se agora na seleção e descubra se este é o caminho certo para você.
          </p>
          <Link
            to={linkSelecao}
            className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white text-base sm:text-lg font-medium px-8 py-4 rounded-full hover:bg-[#1f4f3a] transition-all shadow-lg hover:shadow-xl"
          >
            Quero me inscrever na seleção
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <footer className="py-8 text-center text-xs sm:text-sm text-[#5F5E5A]">
        © {new Date().getFullYear()} Diagnóstico de Saúde e Emoções · Mônica Maria
      </footer>
    </div>
  )
}

export default Capacitacao
